import { useCallback, useRef, useState } from 'react';
import axios from 'src/utils/axios';
import {
  MAX_FILES_PER_MESSAGE,
  rejectionFor,
} from 'src/constants/supportCaseAttachments';
import type { PendingAttachment } from 'src/components/SupportCase/types';

interface UploadResponse {
  id: number;
  name: string;
  content_type: string;
  size: number;
}

export interface AttachmentUploads {
  attachments: PendingAttachment[];
  addFiles: (files: File[]) => Promise<void>;
  remove: (key: string) => void;
  reset: () => void;
  /** Server ids of the files that are actually uploaded and still attached. */
  readyIds: number[];
  uploading: boolean;
  /** A rejection that is not about one file (too many, for example). */
  notice: string | null;
  clearNotice: () => void;
}

const newKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `att-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * The composer's upload queue (ENG-4185).
 *
 * A file is uploaded the moment it is chosen, not when the message is sent. That is
 * the difference between "the send button waits" and "the send button lies": a 12 MB
 * PDF takes seconds, and the message that carries it in this turn's `state.attachments`
 * has to name an id that exists on the server. Send is therefore disabled until every
 * chip is `ready`, which is a fact rather than a guess.
 *
 * Abandoned uploads are the server's problem, not the browser's: an unbound file is
 * deleted after 24 hours (see `attachments.sweep_unbound`). There is no "delete on
 * unmount" here because closing the panel mid-upload should not cancel a file the
 * user may send next time.
 */
export const useAttachmentUploads = (): AttachmentUploads => {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  // The queue is read inside an async loop, so the count check cannot use state.
  const countRef = useRef(0);

  const patch = useCallback(
    (key: string, update: Partial<PendingAttachment>) => {
      setAttachments((current) =>
        current.map((item) =>
          item.key === key ? { ...item, ...update } : item
        )
      );
    },
    []
  );

  const uploadOne = useCallback(
    async (pending: PendingAttachment) => {
      const body = new FormData();
      body.append('file', pending.file, pending.name);
      try {
        const response = await axios.post<UploadResponse>(
          '/support/attachments/',
          body,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        patch(pending.key, {
          status: 'ready',
          id: response.data.id,
          error: null,
        });
      } catch (caught) {
        const detail =
          (caught as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? null;
        patch(pending.key, {
          status: 'failed',
          error:
            detail ??
            'That document could not be uploaded. Remove it and try again.',
        });
      }
    },
    [patch]
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return;
      }
      setNotice(null);

      const accepted: PendingAttachment[] = [];
      const refusals: string[] = [];

      files.forEach((file) => {
        if (countRef.current >= MAX_FILES_PER_MESSAGE) {
          refusals.push(
            `A message can carry up to ${MAX_FILES_PER_MESSAGE} documents.`
          );
          return;
        }
        const rejection = rejectionFor(file);
        if (rejection) {
          refusals.push(rejection);
          return;
        }
        countRef.current += 1;
        accepted.push({
          key: newKey(),
          file,
          name: file.name,
          size: file.size,
          status: 'uploading',
          id: null,
          error: null,
        });
      });

      if (refusals.length > 0) {
        // One sentence, deduplicated: three copies of the allowlist is noise.
        setNotice(Array.from(new Set(refusals)).join(' '));
      }
      if (accepted.length === 0) {
        return;
      }

      setAttachments((current) => [...current, ...accepted]);
      await Promise.all(accepted.map((pending) => uploadOne(pending)));
    },
    [uploadOne]
  );

  const remove = useCallback((key: string) => {
    setAttachments((current) => {
      const next = current.filter((item) => item.key !== key);
      countRef.current = next.length;
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setAttachments([]);
    countRef.current = 0;
    setNotice(null);
  }, []);

  const clearNotice = useCallback(() => setNotice(null), []);

  const readyIds = attachments
    .filter((item) => item.status === 'ready' && item.id !== null)
    .map((item) => item.id as number);
  const uploading = attachments.some((item) => item.status === 'uploading');

  return {
    attachments,
    addFiles,
    remove,
    reset,
    readyIds,
    uploading,
    notice,
    clearNotice,
  };
};

export default useAttachmentUploads;
